/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Layout } from "antd";
import { useSelector } from "react-redux";

import { Auth } from "./Auth";
import TopBar, { BAR_HEIGHT } from './TopBar';
import { useTopBar } from '~/context/TopBarContext';
import Version from './Version';
import OnboardingProgressBar from '~/_shared/components/OnboardingProgressBar';
import MainMenu, { RAIL_WIDTH } from '~/_shared/components/MainMenu';
import { useImpersonate } from '~/context/Impersonate';
import JobProgressBar from '~/components/JobProgressBar';
import TrialBanner from '~/components/TrialBanner';
import MaintenancePage from '~/_shared/components/MaintenancePage';
import { mainteanceRoutes } from "~/_shared/components/MainMenu/routes";
import { useAppContext } from '~/AppContext';

import "./layout.css";
import { getSubdomain } from "~/helpers/getSubdomain";
import { ROUTES } from "~/constants/routes.constants";
import { PERMISSIONS } from "~/constants/permissions.constants";

const { Content } = Layout;

const Main = ({ children, maintenance }) => {
  const location = useLocation();
  const history = useHistory();
  const { isImpersonating, getPreviousUserName, revertImpersonation } = useImpersonate();
  const { isIssueWidgetReady } = useAppContext();
  var topBarCtx = useTopBar();
  var hc = topBarCtx && topBarCtx.headerContent;
  var hasAdvancedBar = !!(hc && hc.advanced);

  const {
    user,
  } = useSelector((state) => ({
    user: state.user,
  }));

  // Search state lifted to layout so TopBar can access it
  const searchState = useState('');
  const searchTerm = searchState[0];
  const setSearchTerm = searchState[1];

  // Menu data from MainMenu
  const menuDataState = useState({ items: [], flatten: function() { return []; } });
  const menuData = menuDataState[0];
  const setMenuData = menuDataState[1];

  const onMenuReady = useCallback(function(items, flattenFn) {
    setMenuData({ items: items, flatten: flattenFn });
  }, []);

  // Build settings pages for search (permission-gated)
  var userPermissions = (user && user.permissions) || [];
  var isAdmin = !!user.id && user.permissions_group_id === 1;
  var hasPerm = function(perm) {
    if (isAdmin) return true;
    return userPermissions.indexOf(perm) !== -1;
  };

  var settingsPages = [
    { key: 'settings-perfil', title: 'Configurações - Perfil', link: '/configuracoes/perfil', icon: 'user', always: true },
    { key: 'settings-senha', title: 'Configurações - Alterar Senha', link: '/configuracoes/senha', icon: 'lock', always: true },
    { key: 'settings-empresa', title: 'Configurações - Dados da Empresa', link: '/configuracoes/empresa/dados', icon: 'bank', perm: PERMISSIONS.settings.empresa.view },
    { key: 'settings-whatsapp-dept', title: 'Configurações - WhatsApp por Departamento', link: '/configuracoes/empresa/whatsapp', icon: 'message', perm: PERMISSIONS.settings.empresa.view },
    { key: 'settings-checklist', title: 'Configurações - Tarefas do Checklist', link: '/configuracoes/tarefas-do-checklist', icon: 'check-square', perm: PERMISSIONS.settings.checklistTasks.view },
    { key: 'settings-cadastro-tarefas', title: 'Configurações - Cadastro de Tarefas', link: '/configuracoes/cadastro-de-tarefas', icon: 'form', perm: PERMISSIONS.settings.taskRegistration.view },
    { key: 'settings-variaveis', title: 'Configurações - Variáveis do Sistema', link: '/configuracoes/variaveis/origens', icon: 'control', perm: PERMISSIONS.settings.systemVariables.view },
    { key: 'settings-origens', title: 'Configurações - Origens', link: '/configuracoes/variaveis/origens', icon: 'global', perm: PERMISSIONS.settings.systemVariables.view },
    { key: 'settings-cadencias-prospeccao', title: 'Configurações - Cadências Prospecção', link: '/configuracoes/variaveis/cadencias-prospeccao', icon: 'rocket', perm: PERMISSIONS.settings.systemVariables.view },
    { key: 'settings-cadencias-social', title: 'Configurações - Cadências Social Selling', link: '/configuracoes/variaveis/cadencias-social-selling', icon: 'share-alt', perm: PERMISSIONS.settings.systemVariables.view },
    { key: 'settings-categorias-pagar', title: 'Configurações - Categorias Contas a Pagar', link: '/configuracoes/variaveis/categorias-contas-pagar', icon: 'wallet', perm: PERMISSIONS.settings.systemVariables.view },
    { key: 'settings-categorias-receber', title: 'Configurações - Categorias Contas a Receber', link: '/configuracoes/variaveis/categorias-contas-receber', icon: 'dollar', perm: PERMISSIONS.settings.systemVariables.view },
    { key: 'settings-mensalidades', title: 'Configurações - Módulo Mensalidades', link: '/configuracoes/mensalidades', icon: 'dollar', perm: PERMISSIONS.settings.monthlyModule.view },
    { key: 'settings-docs', title: 'Configurações - Docs Esporádicos', link: '/configuracoes/variaveis/docs/legalizacao', icon: 'file-text', perm: PERMISSIONS.settings.sporadicDocs.view },
    { key: 'settings-whatsapp', title: 'Configurações - WhatsApp por Departamento', link: '/configuracoes/whatsapp-por-departamento', icon: 'message', perm: PERMISSIONS.settings.integracoes.view },
    { key: 'settings-wpp', title: 'Configurações - Extensão WhatsApp', link: '/configuracoes/wpp', icon: 'api', always: true },
    { key: 'settings-usuarios', title: 'Configurações - Usuários', link: '/configuracoes/usuarios', icon: 'team', perm: PERMISSIONS.users.view },
    { key: 'settings-permissoes', title: 'Configurações - Permissões', link: '/configuracoes/permissoes', icon: 'safety', perm: PERMISSIONS.settings.permissoes.view },
    { key: 'settings-versoes', title: 'Configurações - Histórico de Versões', link: '/configuracoes/versoes', icon: 'history', adminOnly: true },
  ].filter(function(page) {
    if (page.adminOnly) return isAdmin;
    return page.always || hasPerm(page.perm);
  });

  // Compute filtered items for search
  var filteredItems = null;
  if (searchTerm && searchTerm.trim()) {
    var term = searchTerm.toLowerCase().trim();
    var flat = menuData.flatten(menuData.items).concat(settingsPages);
    filteredItems = flat.filter(function(item) {
      return item.title && item.title.toLowerCase().indexOf(term) !== -1;
    });
  }

  var onSearchItemClick = function(item) {
    setSearchTerm('');
    if (item.link) {
      history.push(item.link);
    }
  };

  React.useEffect(function() {
    if (
      location.search.includes("?file=") &&
      (location.pathname === ROUTES["/"] ||
        location.pathname === ROUTES["/login"])
    ) {
      window.location.href = "http://" + getSubdomain() + "." +
        process.env.REACT_APP_CUSTOMER_DOMAIN +
        "/cliente/" + location.search;
    }
  }, []);

  // Track page visits for recent pages
  React.useEffect(function() {
    if (!menuData.items || menuData.items.length === 0) return;
    var path = location.pathname;
    if (path === '/' || path === '/login' || path === '/sair') return;
    var flat = menuData.flatten(menuData.items).concat(settingsPages);
    var match = flat.find(function(item) { return item.link === path; });
    if (match) {
      try {
        var key = 'contlabs_recent_pages';
        var max = 5;
        var data = localStorage.getItem(key);
        var pages = data ? JSON.parse(data) : [];
        pages = pages.filter(function(p) { return p.link !== match.link; });
        pages.unshift({ key: match.key, title: match.title, icon: match.icon, link: match.link });
        if (pages.length > max) pages = pages.slice(0, max);
        localStorage.setItem(key, JSON.stringify(pages));
      } catch (e) { /* ignore */ }
    }
  }, [location.pathname, menuData.items.length]);

  const isMaintenancePage = mainteanceRoutes.includes(location.pathname);
  var impersonating = isImpersonating();
  var impersonateOffset = impersonating ? 28 : 0;

  return (
    <Auth>
      {user.permissions && (
        <div style={{ minHeight: '100vh', background: '#f0f2f5', overflowX: 'hidden' }}>
          {impersonating && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: 28,
              backgroundColor: 'red',
              color: 'white',
              textAlign: 'center',
              padding: '4px',
              fontSize: 13,
              zIndex: 10000
            }}>
              Você está personificando. <a onClick={revertImpersonation} style={{ color: 'white', textDecoration: 'underline', cursor: 'pointer' }}>Voltar para {getPreviousUserName()}</a>
            </div>
          )}
          <TopBar
            user={user}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filteredItems={filteredItems}
            onSearchItemClick={onSearchItemClick}
            isIssueWidgetReady={isIssueWidgetReady}
            topOffset={impersonateOffset}
          />
          <MainMenu
            location={location}
            onMenuReady={onMenuReady}
            topOffset={impersonateOffset}
          />
          <div style={{
            marginLeft: RAIL_WIDTH,
            marginTop: BAR_HEIGHT + impersonateOffset,
            paddingTop: hasAdvancedBar ? 0 : 8,
            minHeight: 'calc(100vh - ' + (BAR_HEIGHT + impersonateOffset) + 'px)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {hasAdvancedBar && <div style={{ height: 44 }} />}
            <JobProgressBar />
            <Content style={{ margin: "0px 16px", flex: 1 }}>
              <TrialBanner />
              <OnboardingProgressBar />
              {isMaintenancePage ? <MaintenancePage /> : children}
            </Content>
          </div>
        </div>
      )}
    </Auth>
  );
};

export default Main;
